import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import api from '../services/api';
import { fetchCurrentUser } from '../services/auth';

const EntryAuthorizationForm = ({ open, onClose }) => {
  const [employees, setEmployees] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [userRole, setUserRole] = useState(null); // 1 for admin, other values for non-admin
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [loading]);

  // Fetch current user and employees
  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentUser = await fetchCurrentUser();
        const roleId = Number(currentUser?.role_id ?? currentUser?.role);
        setUserRole(roleId);
        
        // Fetch employees based on user role
        if (roleId === 1) {
          // ADMIN: afficher tous les employés
          const response = await api.get('/employees');
          // Trier les employés par nom (first_name puis last_name)
          const sortedEmployees = response.data.sort((a, b) => {
            const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
            const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setEmployees(sortedEmployees);
        } else if (roleId === 5) {
          // ADMIN-TENES: afficher les employés du site TENES uniquement
          try {
            const response = await api.get('/employees');
            // Filtrer côté client pour les employés du site TENES
            const tenesEmployees = response.data.filter(emp => 
              emp.site?.name?.toLowerCase().includes('tenes') || 
              emp.site_id?.toString().includes('TENES')
            );
            // Trier les employés par nom (first_name puis last_name)
            const sortedTenesEmployees = tenesEmployees.sort((a, b) => {
              const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
              const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
              return nameA.localeCompare(nameB);
            });
            setEmployees(sortedTenesEmployees);
          } catch (error) {
            console.error('Error filtering TENES employees:', error);
            setEmployees([]);
          }
        } else {
          // AUTRES: afficher le user connecté
          const empResponse = await api.get('/employees/me');
          setEmployee(empResponse.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrorMessage('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchData();
    }
  }, [open]);

  const formik = useFormik({
    initialValues: {
      employeeId: '',
      reason: '',
      entree_date: '',
      entree_time: ''
    },
    validationSchema: Yup.object({
      employeeId: (userRole === 1 || userRole === 5) ? Yup.string().required('L\'employé est requis') : Yup.string(),
      reason: Yup.string().required('Le motif est requis'),
      entree_date: Yup.date().required('La date d\'entrée est requise'),
      entree_time: Yup.string().required('L\'heure d\'entrée est requise')
    }),
    onSubmit: async (values) => {
      if (isSubmitting) {
        return; // Prevent multiple submissions
      }
      
      setErrorMessage('');
      setIsSubmitting(true);
      
      try {
        // For non-admin users, use their own employee ID
        const finalEmployeeId = (userRole === 1 || userRole === 5) ? values.employeeId : (employee ? employee.id.toString() : '');
        
        const payload = {
          ...values,
          employee_id: finalEmployeeId,
          entree_time: values.entree_time
        };

        await api.post('/entry-authorizations', payload);
        
        // Show success message and close dialog instead of showing PDF
        alert('Autorisation d\'entrée créée avec succès!');
        handleClose();
        
      } catch (error) {
        console.error('Error creating entry authorization:', error);
        const errorMsg = error.response?.data?.message || 'Erreur lors de la création de l\'autorisation d\'entrée';
        
        // Handle duplicate error
        if (error.response?.status === 409) {
          setErrorMessage('Une autorisation d\'entrée identique a déjà été créée récemment. Veuillez patienter.');
        } else {
          setErrorMessage(errorMsg);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  // Set employee ID when employee data is loaded (for non-admin users)
  useEffect(() => {
    if (employee && userRole !== 1 && !formik.values.employeeId) {
      formik.setFieldValue('employeeId', employee.id.toString());
    }
  }, [employee, userRole, formik]); // Added formik to dependencies

  const handleClose = () => {
    formik.resetForm();
    setErrorMessage('');
    setIsSubmitting(false);
    onClose();
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
            Chargement...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div">
            Nouvelle Autorisation d'Entrée
          </Typography>
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            {errorMessage && (
              <Typography color="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Typography>
            )}
            
            {(userRole === 1 || userRole === 5) ? (
              <FormControl fullWidth margin="normal" error={formik.touched.employeeId && Boolean(formik.errors.employeeId)}>
                <InputLabel>Employé</InputLabel>
                <Select
                  name="employeeId"
                  value={formik.values.employeeId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.employeeId && formik.errors.employeeId && (
                  <Typography variant="caption" color="error">
                    {formik.errors.employeeId}
                  </Typography>
                )}
              </FormControl>
            ) : (
              <TextField
                fullWidth
                margin="normal"
                label="Employé"
                value={employee ? `${employee.first_name} ${employee.last_name}` : ''}
                disabled
              />
            )}

            <TextField
              fullWidth
              margin="normal"
              label="Motif"
              name="reason"
              value={formik.values.reason}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.reason && Boolean(formik.errors.reason)}
              helperText={formik.touched.reason && formik.errors.reason}
              multiline
              rows={3}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Date d'entrée"
              name="entree_date"
              type="date"
              value={formik.values.entree_date}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.entree_date && Boolean(formik.errors.entree_date)}
              helperText={formik.touched.entree_date && formik.errors.entree_date}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Heure d'entrée"
              name="entree_time"
              type="time"
              value={formik.values.entree_time}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.entree_time && Boolean(formik.errors.entree_time)}
              helperText={formik.touched.entree_time && formik.errors.entree_time}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isSubmitting}>Annuler</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Création en cours...' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default EntryAuthorizationForm;
